import React, { useState, useRef } from "react";
import {
  Modal,
  Box,
  Typography,
  Stack,
  Button,
  CircularProgress,
  TextField,
} from "@mui/material";
import { motion } from "framer-motion";
import { Autocomplete } from "@mui/material";
import { ToastContainer, toast } from "react-toastify";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const EKYCModal = ({ open, onClose, banks, location }) => {
  const [discoverLoading, setDiscoverLoading] = useState(false);
  const [captureLoading, setCaptureLoading] = useState(false);
  const [deviceDiscovered, setDeviceDiscovered] = useState(false);
  const [success, setSuccess] = useState(false);
  const [selectedBank, setSelectedBank] = useState(null);

  const finalUrlRef = useRef(null);

  // discoverAvdm, captureAvdm, submitBiometricData functions here...
  const discoverAvdm = async () => {
    setDiscoverLoading(true);
    let successFlag = false;
    let primaryUrl =
      window.location.protocol === "https:"
        ? "https://127.0.0.1:"
        : "http://127.0.0.1:";

    for (let i = 11100; i <= 11120; i++) {
      try {
        const response = await fetch(primaryUrl + i, {
          method: "RDSERVICE",
          mode: "cors",
          headers: { "Content-Type": "text/xml; charset=utf-8" },
        });

        if (!response.ok) continue;
        const data = await response.text();
        if (data.includes("Mantra")) {
          finalUrlRef.current = primaryUrl + i;
          successFlag = true;
          break;
        }
      } catch (error) {
        console.error("Error discovering AVDM:", error);
      }
    }

    setDiscoverLoading(false);
    if (successFlag) {
      setDeviceDiscovered(true);
      toast.success("Device discovered successfully!");
    } else {
      toast.error("Device discovery failed. Please try again.");
    }
  };

  const captureAvdm = async () => {
    if (!finalUrlRef.current) {
      alert("Please discover AVDM first.");
      return;
    }

    const captureUrl = `${finalUrlRef.current}/rd/capture`;

    const XML = `<?xml version="1.0"?>
                <PidOptions ver="1.0">
                    <Opts fCount="1" fType="2" wadh="E0jzJ/P8UopUHAieZn8CKqS4WPMi5ZSYXgfnlfkWjrc=" iCount="0" pCount="0" format="0" pidVer="2.0"  timeout="10000"  posh="UNKNOWN" env="P" />
                    <CustOpts>
                        <Param name="mantrakey" value="SOME_KEY_VALUE" />
                    </CustOpts>
                </PidOptions>`;

    try {
      const response = await fetch(captureUrl, {
        method: "CAPTURE",
        headers: {
          "Content-Type": "text/xml; charset=utf-8",
        },
        body: XML,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.text();
      console.log("Capture Successful: ", data);

      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(data, "text/xml");

      const resp = xmlDoc.getElementsByTagName("Resp")[0];
      const errInfo = resp.getAttribute("errInfo");

      const deviceInfo = xmlDoc.getElementsByTagName("DeviceInfo")[0];
      const additionalInfo =
        deviceInfo.getElementsByTagName("additional_info")[0];
      const skey = xmlDoc.getElementsByTagName("Skey")[0];
      const pidDataElement = xmlDoc.getElementsByTagName("Data")[0];

      const skeydata = skey.textContent;
      const ci = skey.getAttribute("ci");
      const piddata = pidDataElement.textContent;
      const pidtype = pidDataElement.getAttribute("type");

      const parameters = [];
      const params = additionalInfo.getElementsByTagName("Param");
      for (let i = 0; i < params.length; i++) {
        parameters.push({
          name: params[i].getAttribute("name"),
          value: params[i].getAttribute("value"),
        });
      }

      const extractedData = {
        resp: {
          errCode: resp.getAttribute("errCode"),
          errInfo: errInfo,
          fCount: resp.getAttribute("fCount"),
          fType: resp.getAttribute("fType"),
          nmPoints: resp.getAttribute("nmPoints"),
          qScore: resp.getAttribute("qScore"),
        },
        deviceInfo: {
          dpId: deviceInfo.getAttribute("dpId"),
          rdsId: deviceInfo.getAttribute("rdsId"),
          rdsVer: deviceInfo.getAttribute("rdsVer"),
          mi: deviceInfo.getAttribute("mi"),
          mc: deviceInfo.getAttribute("mc"),
          dc: deviceInfo.getAttribute("dc"),
        },
        additionalInfo: parameters,
        ci: ci,
        skeydata: skeydata,
        hmac: xmlDoc.getElementsByTagName("Hmac")[0].textContent,
        piddata: piddata,
        pidtype: pidtype,
      };

      if (errInfo === "Success.") {
        // Combine extractedData with tfaData
        const combinedPayload = {
          ...extractedData,
          latitude: location.latitude, // Add latitude
          longitude: location.longitude, // Add longitude
        };

        // Call the /tfa API with the combined payload
        await submitBiometricData(combinedPayload);
      } else {
        toast.error(`Capture failed: ${errInfo}`);
      }

      console.log(extractedData);
    } catch (error) {
      console.error("Error capturing data:", error);
      alert("Failed to capture data. Please try again.");
    }
  };
  const submitBiometricData = async (biometricData) => {
    try {
      const token = sessionStorage.getItem("jwtToken");
      if (!token) {
        toast.error("Authentication token missing.");
        return;
      }

      const response = await fetch(
        "https://api.ipaisa.co.in/myapp/auth/cms/merchantBiometricEKyc",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(biometricData),
        }
      );

      const result = await response.json();

      if (result.statusCodeValue === 200) {
        console.log(
          "Biometric KYC submitted successfully:",
          result.body.message
        );
        toast.success(result.body.message);
      } else {
        toast.error(result.body.message);
      }
    } catch (error) {
      console.error("Error submitting biometric KYC:", error);
      toast.error("Failed to complete KYC. Please try again.");
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        component={motion.div}
        initial={{ y: "100%", opacity: 0 }}
        animate={{ y: "0%", opacity: 1 }}
        exit={{ y: "100%", opacity: 0 }}
        transition={{ type: "spring", stiffness: 120, damping: 15 }}
        sx={{
          position: "absolute",
          bottom: 350,
          left: "35%",
          transform: "translate(-50%, 0)",
          width: 420,
          bgcolor: "background.paper",
          boxShadow: 6,
          p: 3,
          borderRadius: 3,
          textAlign: "center",
        }}
      >
        {success ? (
          <CheckCircleIcon color="success" sx={{ fontSize: 60, mt: 1 }} />
        ) : (
          <>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Place your finger on the biometric device.
            </Typography>

            <Autocomplete
              options={banks}
              getOptionLabel={(option) => option.bankName || ""}
              onChange={(_, newValue) => setSelectedBank(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Bank"
                  variant="outlined"
                  fullWidth
                  sx={{ mb: 2 }}
                />
              )}
            />

            <Stack spacing={2} direction="row" justifyContent="center">
              <Button
                variant="contained"
                onClick={discoverAvdm}
                disabled={discoverLoading}
              >
                {discoverLoading ? <CircularProgress size={24} /> : "Discover"}
              </Button>

              <Button
                variant="contained"
                onClick={captureAvdm}
                disabled={!deviceDiscovered || captureLoading || !selectedBank}
              >
                {captureLoading ? <CircularProgress size={24} /> : "Capture"}
              </Button>
            </Stack>
          </>
        )}

        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss={false}
          draggable
          pauseOnHover
          theme="colored"
        />
      </Box>
    </Modal>
  );
};

export default EKYCModal;
